import {
  Archive,
  Code,
  FileSpreadsheet,
  FileText,
  Film,
  Image,
  Music,
  Presentation,
  type LucideIcon,
} from 'lucide-react';
import type * as React from 'react';

import { cn } from '../lib/utils';

/**
 * Literal union for file-type column glyphs.
 *
 * Sourced from PRD §37 / Figma 5:5798 events-grid file-type column:
 * `zip`, `html`, `png`, `jpeg`, `spreadsheet`, `ppt`, `mov`, `mp3`, `pdf`.
 */
export type FileTypeIconName =
  | 'zip'
  | 'html'
  | 'png'
  | 'jpeg'
  | 'spreadsheet'
  | 'ppt'
  | 'mov'
  | 'mp3'
  | 'pdf';

export interface FileTypeIconProps {
  name: FileTypeIconName;
  /** Default 16px to match the events-grid file-type column icon slot. */
  size?: number;
  className?: string;
  /** Optional aria-label. When omitted, the glyph is decorative (`aria-hidden`). */
  'aria-label'?: string;
}

/**
 * Glyph mapping from PRD §37 file-type names to lucide glyphs:
 * - zip          → Archive (compressed archive)
 * - html         → Code (markup / code)
 * - png / jpeg   → Image (raster image)
 * - spreadsheet  → FileSpreadsheet
 * - ppt          → Presentation
 * - mov          → Film (video)
 * - mp3          → Music (audio)
 * - pdf          → FileText (text-bearing document)
 */
const NAME_TO_GLYPH = {
  zip: Archive,
  html: Code,
  png: Image,
  jpeg: Image,
  spreadsheet: FileSpreadsheet,
  ppt: Presentation,
  mov: Film,
  mp3: Music,
  pdf: FileText,
} as const satisfies Readonly<Record<FileTypeIconName, LucideIcon>>;

/**
 * FileTypeIcon — file-extension glyph at the default 16px size matching the
 * Access Legitimacy events grid's file-type column (PRD §37 / Figma 5:5798).
 */
export const FileTypeIcon: React.FC<FileTypeIconProps> = ({
  name,
  size = 16,
  className,
  'aria-label': ariaLabel,
}) => {
  const Glyph = NAME_TO_GLYPH[name];
  return (
    <Glyph
      width={size}
      height={size}
      className={cn(className)}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    />
  );
};

FileTypeIcon.displayName = 'FileTypeIcon';
